import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Box, Link, Typography } from '@mui/material';
import { EditOutlined, NorthEast } from '@mui/icons-material';
import { TaskActions } from '../actions/useTaskActions';
import { dueLabel } from '../tasks/deriveTasks';
import { taskHref } from '../tasks/links';
import { DESTINATION_TABS } from '../tasks/taskActions';
import { ClientTask, DashboardClient } from '../tasks/types';
import { DueTag, INK, panelSx } from '../ui';
import { taskRows } from './todayModel';

/** Task | due in fixed columns, so the due tags line up down the whole day. */
const ROW_GRID = {
  display: 'grid',
  gridTemplateColumns: { xs: 'minmax(0, 1fr) auto', sm: 'minmax(0, 1fr) 92px' },
  alignItems: 'center',
  columnGap: 1.5,
} as const;

const linkSx = { fontWeight: 600, color: 'primary.dark' } as const;

/**
 * A client's open work under their session, one row per thing to do: bookings
 * and assessments grouped, work done in another tab of the client's page as a
 * link there (↗), and a check-in as a link that opens its form in place (pencil).
 */
export const TodayTasks: React.FC<{ client: DashboardClient; tasks: ClientTask[]; actions: TaskActions; now: Date }> = ({ client, tasks, actions, now }) => (
  <Box component="ul" sx={{ ...panelSx, listStyle: 'none', m: 0, mt: 1.5, px: 2, py: 0.25 }}>
    {taskRows(tasks).map((row) => {
      const destination = DESTINATION_TABS[row.lead.kind];
      const label = row.verb ? `${row.verb} ${row.text}` : row.text;
      const isCheckIn = row.lead.kind === 'checkin' && Boolean(row.lead.checkInType);
      return (
        <Box component="li" key={row.key} sx={{ ...ROW_GRID, py: 1.25, '& + &': { borderTop: `1px solid ${INK.faint}` } }}>
          <Typography component="span" sx={{ minWidth: 0, fontSize: 14, fontWeight: 500, color: INK.main }}>
            {destination ? (
              <Link component={RouterLink} to={taskHref(client, row.lead)} aria-label={`${label}, opens ${destination}`} underline="hover" sx={linkSx}>
                {label}
                <NorthEast aria-hidden sx={{ fontSize: 14, ml: 0.5, verticalAlign: '-2px' }} />
              </Link>
            ) : isCheckIn ? (
              <Link
                component="button"
                type="button"
                aria-label={`${label}, opens the check-in form`}
                underline="hover"
                onClick={() => actions.openCheckIn(client, row.lead)}
                sx={{ ...linkSx, font: 'inherit', fontWeight: 600, textAlign: 'left', verticalAlign: 'baseline' }}
              >
                {label}
                <EditOutlined aria-hidden sx={{ fontSize: 14, ml: 0.5, verticalAlign: '-2px' }} />
              </Link>
            ) : row.verb ? (
              <>
                <Box component="span" sx={{ fontWeight: 600 }}>
                  {row.verb}
                </Box>{' '}
                <Box component="span" sx={{ fontWeight: 400, color: INK.body }}>
                  {row.text}
                </Box>
              </>
            ) : (
              row.text
            )}
          </Typography>
          <Box sx={{ justifySelf: 'start' }}>
            <DueTag urgency={row.lead.urgency}>{dueLabel(row.lead, now)}</DueTag>
          </Box>
        </Box>
      );
    })}
  </Box>
);
